/**
 * 账号设置：基本信息、修改密码、登录设备管理。
 * 修改密码成功后后端会吊销全部会话（见 auth.service.changePassword），
 * 因此前端也要清状态并跳登录页——不然用户会看到"页面还在但接口全部 401"。
 *
 * 家长与学生共用本页（`kind` 区分）：**学生也必须能改自己的密码** ——
 * 学生的初始密码是家长创建时生成并口头/抄写转达的，属于"共享过的秘密"，
 * 而且后端在首登时会置 `mustChangePassword`，没有入口就等于这条要求无法完成。
 */
import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PARENT_MIN_PASSWORD_LENGTH,
  STUDENT_MIN_PASSWORD_LENGTH,
  checkPasswordStrength,
  groupSessionsByDevice,
  sessionDeviceKey,
} from '@fht/shared';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { PasswordStrengthMeter } from '../../components/ui/PasswordStrengthMeter';
import { useToast } from '../../components/ui/Toast';
import * as authApi from '../../lib/api/auth';
import { toErrorMessage } from '../../lib/api/error';
import { useAuth } from '../../lib/auth';
import {
  useCleanupSessions,
  useDevices,
  useRevokeDevice,
  useRevokeOtherDevices,
} from '../../lib/queries';
import { formatDateTimeFull, maskEmail } from '../../lib/utils';

export function SettingsPage({ kind = 'parent' }: { kind?: 'parent' | 'student' }) {
  const { user, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const devicesQuery = useDevices();
  const revokeDevice = useRevokeDevice();
  const revokeOtherDevices = useRevokeOtherDevices();
  const cleanupSessions = useCleanupSessions();

  /** 学生密码下限更低（孩子输入成本），与 shared 的策略保持一致。 */
  const isStudent = kind === 'student';
  const minPasswordLength = isStudent ? STUDENT_MIN_PASSWORD_LENGTH : PARENT_MIN_PASSWORD_LENGTH;

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ---------------------------------------------------------------- 登录设备
  //
  // 界面展示的是**设备**，不是会话：一台电脑因为"记住我 + 令牌轮换 + 无痕模式
  // 丢 Cookie"会攒出几十上百条 refresh_session（生产库里一个账号 107 条）。
  // 归并判据（系统 + 浏览器 + IP）来自 @fht/shared，与服务端算 deviceKey 用的是
  // 同一个函数 —— 两边各写一份就会出现"标签说 Edge、分组按 Chrome"这种矛盾。
  const devices = devicesQuery.data?.sessions ?? [];
  const deviceGroups = useMemo(() => groupSessionsByDevice(devices), [devices]);
  const currentSession = devices.find((device) => device.current) ?? null;
  const currentDeviceKey = currentSession ? sessionDeviceKey(currentSession) : null;

  /** 展开看单条会话的设备（默认收起，避免又变成一长串）。 */
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  /** 正在被移除的会话 id（只让那一行转圈，不要整页按钮一起转）。 */
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmOthersOpen, setConfirmOthersOpen] = useState(false);

  /** 除当前设备以外、还能被"批量移除"清掉的会话数。 */
  const otherSessionCount = currentSession
    ? devices.filter(
        (device) => !device.current && sessionDeviceKey(device) !== currentDeviceKey,
      ).length
    : 0;
  const otherDeviceCount = currentDeviceKey
    ? deviceGroups.filter((group) => group.key !== currentDeviceKey).length
    : 0;

  const toggleExpanded = (key: string) => {
    setExpandedKeys((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));
  };

  /** 逐条移除：并发发出，失败时把后端原文透出来（不要吞成"操作失败"）。 */
  const removeSessions = async (ids: string[], label: string) => {
    if (ids.length === 0) return;
    setBusyId(label);
    try {
      await Promise.all(ids.map((id) => revokeDevice.mutateAsync(id)));
      toast.showToast(ids.length > 1 ? `已移除 ${ids.length} 条会话` : '已移除该设备', 'success');
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const onRevokeOthers = async () => {
    try {
      const result = await revokeOtherDevices.mutateAsync();
      setConfirmOthersOpen(false);
      toast.showToast(
        result.revoked > 0
          ? `已移除 ${result.revoked} 条其它设备的登录记录，当前设备保留`
          : '没有需要移除的其它设备',
        'success',
      );
    } catch (err) {
      setConfirmOthersOpen(false);
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  const onCleanup = async () => {
    try {
      const result = await cleanupSessions.mutateAsync();
      toast.showToast(
        result.purged > 0
          ? `已清理 ${result.purged} 条历史记录（当前设备与其它有效设备不受影响）`
          : '没有需要清理的历史记录',
        'success',
      );
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  const check = newPassword
    ? checkPasswordStrength(newPassword, {
        minLength: minPasswordLength,
        identities: [user?.username ?? '', user?.email ?? '', user?.nickname ?? ''],
      })
    : null;

  const onChangePassword = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!currentPassword) {
      setError('请输入当前密码');
      return;
    }
    if (check && !check.ok) {
      setError(check.reason ?? '新密码强度不足');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('两次输入的新密码不一致');
      return;
    }

    setSubmitting(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      toast.showToast('密码已修改，请重新登录', 'success');
      // 后端已吊销全部会话，本地也必须退出，否则会一直 401
      await logout();
      navigate('/login', { replace: true });
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell kind={kind} title="账号设置">
      <div className="space-y-6">
        {/* 首登强制改密的提醒：后端在首次登录时置 mustChangePassword，
            这里必须显式提示，否则学生不会知道"该改密码了"。 */}
        {user?.mustChangePassword && (
          <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
            你现在用的是<strong>家长给的初始密码</strong>。为了安全，请立刻在下面改成只有你自己知道的密码。
          </p>
        )}

        {/* ---------- 基本信息 ---------- */}
        <Card>
          <CardHeader
            title="我的账号"
            description={isStudent ? '这是你的登录账号。' : '身份标签只用于显示，不参与权限判断。'}
          />
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-slate-500">昵称</dt>
              <dd className="font-medium text-slate-900">{user?.nickname ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-slate-500">用户名</dt>
              <dd className="font-medium text-slate-900">{user?.username ?? '—'}</dd>
            </div>
            {/* 身份 / 邮箱 / TOTP 是家长账号才有的概念，学生看到只会困惑 */}
            {!isStudent && (
              <>
                <div>
                  <dt className="text-slate-500">身份</dt>
                  <dd className="font-medium text-slate-900">{user?.relationLabel ?? '家长'}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">邮箱</dt>
                  <dd className="font-medium text-slate-900">{maskEmail(user?.email) || '—'}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">两步验证（TOTP）</dt>
                  <dd className="font-medium text-slate-900">
                    {user?.totpEnabled ? '已开启' : '未开启'}
                  </dd>
                </div>
              </>
            )}
          </dl>
        </Card>

        {/* ---------- 修改密码 ---------- */}
        <Card>
          <CardHeader
            title="修改密码"
            description={`至少 ${minPasswordLength} 位；修改后其他设备需要重新登录。`}
          />
          <form onSubmit={onChangePassword} noValidate className="space-y-4">
            <Input
              label="当前密码"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
            <div className="space-y-2">
              <Input
                label="新密码"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <PasswordStrengthMeter password={newPassword} />
              {check && !check.ok && <p className="text-xs text-amber-600">{check.reason}</p>}
            </div>
            <Input
              label="确认新密码"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              error={
                confirmPassword && confirmPassword !== newPassword ? '两次输入不一致' : undefined
              }
            />

            {error && (
              <div role="alert" className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div className="flex justify-end">
              <Button type="submit" loading={submitting}>
                保存新密码
              </Button>
            </div>
          </form>
        </Card>

        {/* ---------- 登录设备 ---------- */}
        <Card>
          <CardHeader
            title="登录设备"
            description="同一台设备（浏览器 + IP）归并成一行。勾选「下次不用输密码」的设备会保留 30 天会话；发现陌生设备请立即移除。"
            action={
              devices.length > 0 ? (
                <span className="shrink-0 text-xs text-slate-400">
                  {deviceGroups.length} 台设备 · {devices.length} 条会话
                </span>
              ) : undefined
            }
          />

          {devicesQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : devices.length === 0 ? (
            <EmptyState
              icon="📱"
              title="暂无登录设备"
              description="这里列出你所有还能免密登录的设备。当前这条会话一旦过期，页面会回到登录页。"
            />
          ) : (
            <>
              {/*
                批量操作区。安全说明必须写在界面上，而不是只写在代码注释里 ——
                用户点"移除其它全部设备"之前就得知道**本机不会被移除**。
              */}
              <div className="mb-3 rounded-xl bg-slate-50 px-3 py-2.5">
                <p className="text-xs text-slate-500">
                  当前设备（本机）会<strong className="text-slate-700">始终保留</strong>
                  ，批量移除只影响其它设备，你不会被踢下线。
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!currentSession || otherSessionCount === 0}
                    onClick={() => setConfirmOthersOpen(true)}
                  >
                    移除其它全部设备
                    {otherDeviceCount > 0 ? `（${otherDeviceCount} 台）` : ''}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={cleanupSessions.isPending}
                    onClick={() => void onCleanup()}
                  >
                    清理无效记录
                  </Button>
                </div>
                {!currentSession && (
                  <p className="mt-2 text-xs text-amber-700">
                    没能确认你当前用的是哪台设备（登录凭证可能刚被刷新过）。为避免把你自己踢下线，批量移除已停用
                    —— 请刷新页面后重试。
                  </p>
                )}
              </div>

              <ul className="divide-y divide-slate-100">
                {deviceGroups.map((group) => {
                  const isCurrentDevice = group.key === currentDeviceKey;
                  const removable = group.sessions.filter((device) => !device.current);
                  const expanded = expandedKeys.includes(group.key);
                  const groupBusy = busyId === group.key;
                  return (
                    <li key={group.key} className="py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="flex flex-wrap items-center gap-2">
                            <span className="truncate font-medium text-slate-900">
                              {group.label}
                            </span>
                            {isCurrentDevice && <Badge tone="brand">本机</Badge>}
                            {group.rememberDevice && (
                              <span className="text-xs font-normal text-brand-600">免密设备</span>
                            )}
                          </p>
                          <p className="text-xs text-slate-500">
                            最近使用 {formatDateTimeFull(group.lastUsedAt)}
                            {group.ip ? ` · ${group.ip}` : ''}
                            {group.sessionCount > 1 ? ` · 共 ${group.sessionCount} 条会话` : ''}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          {group.sessionCount > 1 && (
                            <Button variant="ghost" size="sm" onClick={() => toggleExpanded(group.key)}>
                              {expanded ? '收起' : '明细'}
                            </Button>
                          )}
                          {removable.length > 0 && (
                            <Button
                              variant="secondary"
                              size="sm"
                              loading={groupBusy}
                              onClick={() =>
                                void removeSessions(
                                  removable.map((device) => device.id),
                                  group.key,
                                )
                              }
                            >
                              {isCurrentDevice ? `移除其它 ${removable.length} 条` : '移除此设备'}
                            </Button>
                          )}
                        </div>
                      </div>

                      {expanded && (
                        <ul className="mt-2 space-y-1.5 rounded-xl bg-slate-50 p-2">
                          {group.sessions.map((device) => (
                            <li
                              key={device.id}
                              className="flex items-center justify-between gap-2 rounded-lg bg-white px-2.5 py-1.5"
                            >
                              <span className="min-w-0 truncate text-xs text-slate-500">
                                登录 {formatDateTimeFull(device.lastUsedAt)}
                                {device.rememberDevice ? ' · 免密 30 天' : ''}
                                {device.expiresAt
                                  ? ` · 有效至 ${formatDateTimeFull(device.expiresAt)}`
                                  : ''}
                              </span>
                              {/* 当前会话没有"移除"按钮：它由「退出登录」负责下线 */}
                              {device.current ? (
                                <span className="shrink-0 text-xs font-medium text-brand-600">
                                  本机
                                </span>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  loading={busyId === device.id}
                                  onClick={() => void removeSessions([device.id], device.id)}
                                >
                                  移除
                                </Button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </Card>

        {/* 批量移除前的二次确认：把"会删几条 / 本机不会被删"讲清楚 */}
        <Modal
          open={confirmOthersOpen}
          title="移除其它全部设备"
          description="被移除的设备下次打开需要重新输入密码。"
          onClose={() => setConfirmOthersOpen(false)}
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirmOthersOpen(false)}>
                取消
              </Button>
              <Button
                variant="danger"
                loading={revokeOtherDevices.isPending}
                onClick={() => void onRevokeOthers()}
              >
                确认移除
              </Button>
            </>
          }
        >
          <p className="text-sm text-slate-600">
            将移除 <strong>{otherDeviceCount}</strong> 台其它设备上的{' '}
            <strong>{otherSessionCount}</strong> 条登录记录。
          </p>
          <p className="mt-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            当前设备（本机
            {currentSession?.deviceLabel ? ` · ${currentSession.deviceLabel}` : ''}）不会受影响，
            你不会被踢下线。
          </p>
          <p className="mt-2 text-xs text-slate-500">
            已经在其它设备上打开、还没过期的页面最长还能用 15 分钟，之后就需要重新登录。
          </p>
        </Modal>

        <Card>
          <CardHeader title="两步验证（TOTP）" description="家长账号可开启，孩子账号不需要。" />
          <p className="text-sm text-slate-500">
            接口已就绪（POST /auth/totp/setup、/auth/totp/verify），界面下一步补齐：
            显示二维码 → 输入 6 位动态口令 → 开启。
          </p>
        </Card>
      </div>
    </AppShell>
  );
}
